import React, { useState, useEffect, useCallback, useRef } from "react";
import { collection, getDocs, doc, getDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../utils/firebase";
import { useAuth } from "../utils/authContext";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Chip from "@mui/material/Chip";
import Popover from "@mui/material/Popover";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Tooltip from "@mui/material/Tooltip";
import { ArrowLeft, GripVertical, X, Plus, Save, Settings, GitBranch, Layout, Edit2, AlertTriangle, FileText, Lock, Unlock, Send, StickyNote } from "lucide-react";
import ModalPortal from "../utils/ModalPortal";
import AdminNoteEditor from "./components/AdminNoteEditor";
import SchemaFormRenderer from "./SchemaFormRenderer";
import ItemFormDialog from "./ItemFormDialog";
import { hasZeroPrerequisiteSummary } from "../utils/documentTypeValidation";
import { useUnsavedChanges } from './useUnsavedChanges';
import { sortByDisplayLabel } from './sortByDisplayLabel';
import { applySchemaDefaults } from './schemaDefaults';
import { saveConfigurationRevision } from './saveConfigurationRevision';
import { initializeFieldBinding } from './dataFieldBinding';
import { cleanConfigurationData as cleanForFirestore, cleanConfigurationSnapshot, configurationSnapshot } from './configurationHistory';

const SCHEMA_DOC_PATH = "globalSettings/documentBuilder";

// Helper to convert display name to camelCase ID
const toCamelCase = (str) => {
  if (!str) return "";
  return str
    .replace(/[^a-zA-Z0-9\s]/g, "") // Remove special chars
    .split(/\s+/)
    .filter(Boolean)
    .map((word, index) => {
      if (index === 0) {
        return word.toLowerCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join("");
};

// Helper to convert display name to snake_case field name
const toSnakeCase = (str) => {
  if (!str) return "";
  return str
    .replace(/[^a-zA-Z0-9\s]/g, "") // Remove special chars
    .split(/\s+/)
    .filter(Boolean)
    .map(word => word.toLowerCase())
    .join("_");
};

// Subcategory definitions - these map to parent categories
const SUBCATEGORIES = [
  { value: "initial-note", label: "Initial Note", category: 1 },
  { value: "psychiatric-progress", label: "Psychiatric Progress Note", category: 1 },
  { value: "psychotherapy-progress", label: "Psychotherapy Progress Note", category: 1 },
  { value: "assessment", label: "Assessment Note", category: 1 },
  { value: "care-coordination", label: "Care Coordination Note", category: 1 },
  { value: "document-importing", label: "Document Importing", category: 2 },
  { value: "summary-document", label: "Summary Document", category: 3 },
];

// Parent category mapping (hidden from user, derived from subcategory)
const CATEGORY_FROM_SUBCATEGORY = SUBCATEGORIES.reduce((acc, sub) => {
  acc[sub.value] = sub.category;
  return acc;
}, {});

// Format options
const FORMATS = [
  { value: "audio", label: "Audio" },
  { value: "upload", label: "Upload" },
  { value: "summary", label: "Summary" },
];

// Menu visibility options
const MENU_OPTIONS = [
  { value: "default", label: "Default (Production)" },
  { value: "beta", label: "Beta" },
  { value: "development", label: "Development Only" },
];

// Export options
const EXPORT_OPTIONS = [
  { value: "doctor", label: "Doctor" },
  { value: "patient", label: "Patient" },
];

// Drag-and-drop item types
const ItemTypes = {
  STEP: "step",
  SECTION: "section",
};

// Step type color coding using CSS variables
const STEP_TYPE_COLORS = {
  submitToVertexAI: { bg: "var(--color-workflow-ai-bg)", border: "var(--color-workflow-ai-border)", label: "AI Processing" },
  empty: { bg: "var(--color-workflow-empty-bg)", border: "var(--color-workflow-empty-border)", label: "Empty/Skip" },
  continue: { bg: "var(--color-workflow-continue-bg)", border: "var(--color-workflow-continue-border)", label: "Continue" },
  history: { bg: "var(--color-workflow-history-bg)", border: "var(--color-workflow-history-border)", label: "History" },
  default: { bg: "var(--color-workflow-default-bg)", border: "var(--color-workflow-default-border)", label: "Step" },
};

// Section type color coding using CSS variables
const SECTION_TYPE_COLORS = {
  EditableTextSection: { bg: "var(--color-workflow-text-bg)", border: "var(--color-workflow-text-border)", label: "Text" },
  EditableTableSection: { bg: "var(--color-workflow-table-bg)", border: "var(--color-workflow-table-border)", label: "Table" },
  EditableListSection: { bg: "var(--color-workflow-list-bg)", border: "var(--color-workflow-list-border)", label: "List" },
  StaticSection: { bg: "var(--color-workflow-static-bg)", border: "var(--color-workflow-static-border)", label: "Static" },
  Confirmation: { bg: "var(--color-workflow-confirmation-bg)", border: "var(--color-workflow-confirmation-border)", label: "Confirm" },
  StartEndTime: { bg: "var(--color-workflow-therapy-bg)", border: "var(--color-workflow-therapy-border)", label: "Time" },
  TherapyTiming: { bg: "var(--color-workflow-therapy-bg)", border: "var(--color-workflow-therapy-border)", label: "Therapy" },
  TreatmentInformation: { bg: "var(--color-workflow-treatment-bg)", border: "var(--color-workflow-treatment-border)", label: "Treatment" },
  default: { bg: "var(--color-workflow-default-bg)", border: "var(--color-workflow-default-border)", label: "Section" },
};

// Arrow connector between workflow items
const ArrowConnector = () => (
  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 0.5 }}>
    <Box sx={{ width: 2, height: 16, bgcolor: "var(--color-border-secondary)" }} />
    <Box
      sx={{
        width: 0,
        height: 0,
        borderLeft: "6px solid transparent",
        borderRight: "6px solid transparent",
        borderTop: "8px solid var(--color-border-secondary)",
      }}
    />
  </Box>
);

// Draggable item from the available palette
const DraggablePaletteItem = ({ item, type, isStep = false }) => {
  const colorMap = isStep ? STEP_TYPE_COLORS : SECTION_TYPE_COLORS;
  const colors = colorMap[item.type] || colorMap.default;
  
  const [{ isDragging }, drag] = useDrag({
    type,
    item: () => ({ ...item, isNew: true }),
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  return (
    <Box
      ref={drag}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        py: 0.5,
        px: 0.5,
        cursor: "grab",
        borderRadius: 0.5,
        borderLeft: `3px solid ${colors.border}`,
        mb: 0.5,
        bgcolor: colors.bg,
        opacity: isDragging ? 0.4 : 1,
        "&:hover": { boxShadow: 1 },
      }}
    >
      <GripVertical size={12} style={{ flexShrink: 0, color: colors.border }} />
      <Typography variant="caption" noWrap sx={{ flex: 1 }}>
        {item.displayName || item.title || item.name || item.id}
      </Typography>
    </Box>
  );
};

// Drop zone for inserting new items at a specific position
const DropZone = ({ index, type, onInsert, isInitial = false, isBetween = false }) => {
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: type,
    canDrop: (draggedItem) => draggedItem.isNew,
    drop: (draggedItem) => {
      if (draggedItem.isNew) {
        onInsert(draggedItem, index);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  // Large initial drop zone for empty workflows
  if (isInitial) {
    return (
      <Box
        ref={drop}
        sx={{
          width: "100%",
          minHeight: 120,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          transition: "all 0.2s ease",
          borderRadius: 2,
          bgcolor: isOver && canDrop ? "var(--color-primary-light)" : "transparent",
          border: isOver && canDrop ? "2px dashed var(--color-primary)" : "1px dashed rgba(0, 0, 0, 0.12)",
        }}
      >
        <Plus size={24} style={{ color: isOver && canDrop ? "var(--color-primary)" : "rgba(0, 0, 0, 0.2)" }} />
        <Typography variant="body2" sx={{ color: isOver && canDrop ? "primary.main" : "rgba(0, 0, 0, 0.3)" }}>
          {isOver && canDrop ? "Drop to add" : "Drag items here to start"}
        </Typography>
      </Box>
    );
  }

  // Middle drop zones - hidden until dragging over
  if (isBetween) {
    return (
      <Box
        ref={drop}
        sx={{
          width: 380,
          height: isOver && canDrop ? 48 : 16,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.15s ease",
          borderRadius: 1,
          ml: 6,
          bgcolor: isOver && canDrop ? "var(--color-primary-light)" : "transparent",
          border: isOver && canDrop ? "2px dashed var(--color-primary)" : "2px dashed transparent",
        }}
      >
        {isOver && canDrop && (
          <Typography variant="caption" color="primary">
            Drop here
          </Typography>
        )}
      </Box>
    );
  }

  // Bottom drop zone - always visible
  return (
    <Box
      ref={drop}
      sx={{
        width: 380,
        height: isOver && canDrop ? 56 : 32,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "all 0.15s ease",
        borderRadius: 1,
        bgcolor: isOver && canDrop ? "var(--color-primary-light)" : "transparent",
        border: isOver && canDrop ? "2px dashed var(--color-primary)" : "1px dashed rgba(0, 0, 0, 0.12)",
      }}
    >
      <Typography variant="caption" sx={{ color: isOver && canDrop ? "primary.main" : "rgba(0, 0, 0, 0.3)" }}>
        {isOver && canDrop ? "Drop here" : "Drop to insert"}
      </Typography>
    </Box>
  );
};

// Workflow card with color coding
const WorkflowCard = ({ item, index, moveItem, removeItem, onEdit, onViewPrompt, type, isStep = false, prompts = [] }) => {
  const ref = useRef(null);
  const colorMap = isStep ? STEP_TYPE_COLORS : SECTION_TYPE_COLORS;
  const colors = colorMap[item.type] || colorMap.default;
  
  // Look up response time from prompt's lastTestStats
  const promptData = isStep && item.promptName ? prompts.find(p => p.id === item.promptName) : null;
  const responseTime = promptData?.lastTestStats?.responseTime;

  const [{ isDragging }, drag] = useDrag({
    type,
    item: () => ({ id: item.id, index }),
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const [{ isOver }, drop] = useDrop({
    accept: type,
    hover(draggedItem) {
      // Skip if dragging a new item from palette - let DropZone handle those
      if (draggedItem.isNew) return;
      if (!ref.current || draggedItem.index === index) return;
      moveItem(draggedItem.index, index);
      draggedItem.index = index;
    },
    collect: (monitor) => ({ isOver: monitor.isOver() }),
  });

  drag(drop(ref));

  return (
    <Paper
      ref={ref}
      elevation={isOver ? 4 : 1}
      sx={{
        p: 2,
        width: 380,
        cursor: "grab",
        opacity: isDragging ? 0.4 : 1,
        borderLeft: `4px solid ${colors.border}`,
        bgcolor: colors.bg,
        transition: "all 0.15s ease",
        "&:hover": {
          boxShadow: 3,
          "& .action-btns": { opacity: 1 },
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
        <GripVertical size={14} style={{ color: colors.border, flexShrink: 0, marginTop: 2 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Chip 
            label={colors.label} 
            size="small" 
            sx={{ 
              height: 16, 
              fontSize: "var(--text-2xs)",
              bgcolor: "var(--color-content-background)",
              border: `1px solid ${colors.border}`,
              color: colors.border,
              mb: 0.5,
              "& .MuiChip-label": { px: 0.75 },
            }} 
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
              {item.displayName || item.title || item.name || item.id}
            </Typography>
            {item.isLocked && (
              <Tooltip title="Published & Locked" arrow>
                <Lock size={12} style={{ color: 'var(--color-text-secondary)', flexShrink: 0 }} />
              </Tooltip>
            )}
          </Box>
          {promptData?.model && (
            <Typography 
              variant="caption" 
              sx={{ 
                fontSize: "var(--text-2xs)", 
                color: "text.secondary",
                display: "block",
                mt: 0.25,
              }}
            >
              {promptData.model}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
          <IconButton size="small" onClick={() => onEdit(item)} title="Edit" sx={{ p: 0.5 }}>
            <Edit2 size={14} />
          </IconButton>
          {isStep && (item.prompt || item.promptName) && (
            <IconButton size="small" onClick={() => onViewPrompt(item)} title="Preview Prompt" sx={{ p: 0.5 }}>
              <FileText size={14} />
            </IconButton>
          )}
          <IconButton size="small" onClick={() => removeItem(index)} title="Remove" sx={{ p: 0.5 }}>
            <X size={14} />
          </IconButton>
        </Box>
      </Box>
      {responseTime && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5, pr: 0.5 }}>
          <Typography 
            variant="caption" 
            sx={{ 
              fontSize: "var(--text-xs)",
              fontWeight: 600,
              color: parseFloat(responseTime) < 20 
                ? 'success.main' 
                : parseFloat(responseTime) <= 30 
                  ? 'warning.main' 
                  : 'error.main'
            }}
          >
            {responseTime}s
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

// Timeline node with step number and connecting lines
const TimelineNode = ({ number, isFirst, isLast, colors }) => (
  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", alignSelf: "stretch" }}>
    {/* Line from previous node */}
    <Box
      sx={{
        width: 2,
        height: 16,
        bgcolor: isFirst ? "transparent" : "var(--color-border-secondary)",
      }}
    />
    {/* Numbered circle */}
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: "50%",
        bgcolor: colors.bg,
        border: `2px solid ${colors.border}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 600,
        fontSize: "var(--text-xs)",
        color: colors.border,
        flexShrink: 0,
        zIndex: 1,
      }}
    >
      {number}
    </Box>
    {/* Line to next node */}
    <Box
      sx={{
        width: 2,
        flex: 1,
        minHeight: 16,
        bgcolor: isLast ? "transparent" : "var(--color-border-secondary)",
      }}
    />
  </Box>
);

// Workflow flow container with timeline rail
const WorkflowFlow = ({ items, moveItem, removeItem, onEditItem, onViewPrompt, type, isStep = false, onInsertAt, prompts = [] }) => {
  const colorMap = isStep ? STEP_TYPE_COLORS : SECTION_TYPE_COLORS;

  if (items.length === 0) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", p: 3 }}>
        {onInsertAt && (
          <DropZone index={0} type={type} onInsert={onInsertAt} isInitial />
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ py: 2, width: "100%", position: "relative" }}>
      {/* Drop zone at the start */}
      {onInsertAt && (
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2, ml: 5 }}>
          <DropZone index={0} type={type} onInsert={onInsertAt} />
        </Box>
      )}
      
      {/* Timeline + Cards rows */}
      {items.map((item, index) => {
        const colors = colorMap[item.type] || colorMap.default;
        const isLast = index === items.length - 1;
        return (
          <Box key={item.id + "-" + index}>
            {/* Row: timeline node + card */}
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 2 }}>
              {/* Timeline node */}
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  bgcolor: colors.bg,
                  border: `2px solid ${colors.border}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 600,
                  fontSize: "var(--text-xs)",
                  color: colors.border,
                  flexShrink: 0,
                }}
              >
                {index + 1}
              </Box>
              {/* Card */}
              <WorkflowCard
                item={item}
                index={index}
                moveItem={moveItem}
                removeItem={removeItem}
                onEdit={onEditItem}
                onViewPrompt={onViewPrompt}
                type={type}
                isStep={isStep}
                prompts={prompts}
              />
            </Box>
            
            {/* Connecting line + drop zone */}
            {!isLast && (
              <Box sx={{ display: "flex", justifyContent: "center", ml: -49 }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <Box sx={{ width: 2, height: 12, bgcolor: "var(--color-border-secondary)" }} />
                  {onInsertAt && <DropZone index={index + 1} type={type} onInsert={onInsertAt} isBetween />}
                  <Box sx={{ width: 2, height: 12, bgcolor: "var(--color-border-secondary)" }} />
                </Box>
              </Box>
            )}
          </Box>
        );
      })}
      
      {/* Drop zone at the end */}
      {onInsertAt && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2, ml: 5 }}>
          <DropZone index={items.length} type={type} onInsert={onInsertAt} />
        </Box>
      )}
    </Box>
  );
};

// Compact available items sidebar
const AvailablePalette = ({ items, selectedIds, onAdd, label, isStep = false, type, onCreateNew }) => {
  const [search, setSearch] = useState("");
  const colorMap = isStep ? STEP_TYPE_COLORS : SECTION_TYPE_COLORS;
  
  const availableItems = items.filter(item => {
    const notSelected = !selectedIds.includes(item.id);
    const matchesSearch = !search || 
      (item.displayName || item.title || item.name || item.id)
        .toLowerCase()
        .includes(search.toLowerCase());
    return notSelected && matchesSearch;
  }).sort((a, b) => {
    const nameA = (a.displayName || a.title || a.name || a.id).toLowerCase().trim();
    const nameB = (b.displayName || b.title || b.name || b.id).toLowerCase().trim();
    return nameA.localeCompare(nameB);
  });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="subtitle2">
          Available ({availableItems.length})
        </Typography>
        {onCreateNew && (
          <Button
            size="small"
            startIcon={<Plus size={14} />}
            onClick={onCreateNew}
            sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: "var(--text-xs)" }}
          >
            New
          </Button>
        )}
      </Box>
      <TextField
        size="small"
        placeholder={`Search...`}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        fullWidth
        sx={{ mb: 1 }}
      />
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {availableItems.length === 0 ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", py: 1, textAlign: "center" }}>
            {search ? "No matches" : "All added"}
          </Typography>
        ) : (
          availableItems.map(item => (
            <DraggablePaletteItem
              key={item.id}
              item={item}
              type={type}
              isStep={isStep}
            />
          ))
        )}
      </Box>
    </Box>
  );
};

/**
 * Prerequisites Editor - allows configuring required document category counts
 */
const PrerequisitesEditor = ({ prerequisites = [], currentSubcategory, onChange, warnWhenEmpty = false }) => {
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedCount, setSelectedCount] = useState(1);

  // Filter out current subcategory and already-added prerequisites
  const addedCategories = prerequisites.map(p => p.requiredSubcategory);
  const availableCategories = sortByDisplayLabel(
    SUBCATEGORIES.filter(sub => sub.value !== currentSubcategory && !addedCategories.includes(sub.value)),
    category => category.label
  );

  const handleAdd = () => {
    if (!selectedCategory) return;
    const newPrereq = {
      requiredSubcategory: selectedCategory,
      minCount: selectedCount,
    };
    onChange([...prerequisites, newPrereq]);
    setSelectedCategory("");
    setSelectedCount(1);
  };

  const handleRemove = (subcategory) => {
    onChange(prerequisites.filter(p => p.requiredSubcategory !== subcategory));
  };

  const getCategoryLabel = (subcategory) => {
    const cat = SUBCATEGORIES.find(s => s.value === subcategory);
    return cat?.label || subcategory;
  };

  return (
    <Box sx={{ gridColumn: "span 4", mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Prerequisites</Typography>
      <Typography variant="caption" color="textSecondary" sx={{ display: "block", mb: 1.5 }}>
        Require documents from other categories before this document can be created.
      </Typography>
      
      <Box sx={{ 
        border: "1px solid var(--color-border)", 
        borderRadius: 1, 
        p: 2,
        bgcolor: "var(--color-light-background)"
      }}>
        {/* Add new prerequisite controls */}
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end", mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
            <InputLabel>Document Category</InputLabel>
            <Select
              value={selectedCategory}
              label="Document Category"
              onChange={(e) => setSelectedCategory(e.target.value)}
              disabled={availableCategories.length === 0}
            >
              {availableCategories.length === 0 ? (
                <MenuItem disabled value="">
                  All categories configured
                </MenuItem>
              ) : (
                availableCategories.map(cat => (
                  <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          
          <TextField
            label="Count"
            type="number"
            size="small"
            value={selectedCount}
            onChange={(e) => setSelectedCount(Math.max(1, parseInt(e.target.value) || 1))}
            slotProps={{ input: { inputProps: { min: 1 } } }}
            sx={{ width: 80 }}
          />
          
          <Button
            variant="outlined"
            size="small"
            startIcon={<Plus size={16} />}
            onClick={handleAdd}
            disabled={!selectedCategory}
            sx={{ height: 40 }}
          >
            Add
          </Button>
        </Box>
        
        {/* List of configured prerequisites */}
        {prerequisites.length === 0 ? (
          warnWhenEmpty ? (
            <Alert severity="warning">
              <strong>No prerequisites configured.</strong> This summary type will not be able to generate until at least one prerequisite is added.
            </Alert>
          ) : (
            <Typography variant="body2" color="textSecondary" sx={{ fontStyle: "italic" }}>
              No prerequisites configured
            </Typography>
          )
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {prerequisites.map((prereq) => (
              <Box 
                key={prereq.requiredSubcategory} 
                sx={{ 
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "space-between",
                  bgcolor: "var(--color-background)",
                  border: "1px solid var(--color-border)",
                  borderRadius: 1,
                  px: 2,
                  py: 1
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {getCategoryLabel(prereq.requiredSubcategory)}
                  </Typography>
                  <Chip 
                    label={`${prereq.minCount} required`} 
                    size="small" 
                    variant="outlined"
                  />
                </Box>
                <IconButton 
                  size="small" 
                  onClick={() => handleRemove(prereq.requiredSubcategory)}
                  sx={{ color: "var(--color-text-secondary)" }}
                >
                  <X size={16} />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

/**
 * Full-page editor for a single document type
 */
const AdminDocumentTypeEditor = ({ documentType, isNew, onBack, onSave, onDirtyChange }) => {
  const { currentUser } = useAuth();
  const savedDocumentType = useRef(documentType);
  const [steps, setSteps] = useState([]);
  const [sections, setSections] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [schemas, setSchemas] = useState({ stepSchema: null, sectionSchema: null });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [zeroPrerequisiteDialogOpen, setZeroPrerequisiteDialogOpen] = useState(false);
  const [error, setError] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  const confirmDiscard = useUnsavedChanges(hasChanges);
  useEffect(() => { onDirtyChange?.(hasChanges); }, [hasChanges, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);
  const handleBack = () => {
    if (confirmDiscard()) onBack();
  };
  // Default to Settings tab (2) for new docs, Steps tab (0) for existing
  const [activeTab, setActiveTab] = useState(isNew ? 2 : 0);

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingItemType, setEditingItemType] = useState(null); // "step" or "section"
  const [editFormData, setEditFormData] = useState({});
  const [isEditSaving, setIsEditSaving] = useState(false);
  const [editSessionUnlocked, setEditSessionUnlocked] = useState(false);
  const [editUnlockDialogOpen, setEditUnlockDialogOpen] = useState(false);
  const [isEditPublishing, setIsEditPublishing] = useState(false);

  // Handler for edit form changes that supports nested keys (e.g., "message.title")
  const handleEditFormChange = useCallback((key, value) => {
    setEditFormData(prev => {
      const next = { ...prev };
      const parts = key.split('.');
      if (parts.length === 1) {
        // Simple key
        next[key] = value;
      } else {
        // Nested key - create nested structure
        let current = next;
        for (let i = 0; i < parts.length - 1; i++) {
          if (!current[parts[i]] || typeof current[parts[i]] !== 'object') {
            current[parts[i]] = {};
          } else {
            // Clone the nested object to avoid mutating prev
            current[parts[i]] = { ...current[parts[i]] };
          }
          current = current[parts[i]];
        }
        current[parts[parts.length - 1]] = value;
      }
      return next;
    });
  }, []);

  // Create modal state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createItemType, setCreateItemType] = useState(null); // "step" or "section"

  // Prompt view modal state
  const [promptViewOpen, setPromptViewOpen] = useState(false);
  const [viewingPromptItem, setViewingPromptItem] = useState(null);
  
  // Versioning state
  const [isPublishing, setIsPublishing] = useState(false);
  const [sessionUnlocked, setSessionUnlocked] = useState(false);
  const [unlockDialogOpen, setUnlockDialogOpen] = useState(false);
  const [savedVersion, setSavedVersion] = useState(null); // Track version after saves
  
  // Version/lock status - use savedVersion if available (after local save), otherwise from prop
  const currentVersion = savedVersion ?? documentType?.version ?? 1;
  const publishedVersion = documentType?.publishedVersion || null;
  const isLocked = documentType?.isLocked && !sessionUnlocked;
  const hasUnpublishedChanges = !publishedVersion || currentVersion !== publishedVersion;

  // Form state
  const [formData, setFormData] = useState({
    id: documentType?.id || "",
    name: documentType?.name || "",
    description: documentType?.description || "",
    format: documentType?.format || "audio",
    category: documentType?.category || 1,
    subcategory: documentType?.subcategory || "initial-note",
    eta: documentType?.eta || 120,
    status: documentType?.status || "draft",
    exports: documentType?.exports || ["doctor", "patient"],
    menu: documentType?.menu || ["beta", "default"],
    stepWorkflow: documentType?.stepWorkflow || [],
    sectionLayout: documentType?.sectionLayout || [],
    notes: documentType?.notes || "",
    prerequisites: documentType?.prerequisites || [],
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch steps for workflow picker
      const stepsSnapshot = await getDocs(collection(db, "documentSteps"));
      const stepsData = stepsSnapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
      setSteps(stepsData);

      // Fetch sections for layout picker
      const sectionsSnapshot = await getDocs(collection(db, "documentSections"));
      const sectionsData = sectionsSnapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
      setSections(sectionsData);

      // Fetch prompts for prompt preview
      const promptsSnapshot = await getDocs(collection(db, "prompts"));
      const promptsData = promptsSnapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
      setPrompts(promptsData);

      // Fetch schemas for edit form
      const schemaDoc = await getDoc(doc(db, SCHEMA_DOC_PATH));
      if (schemaDoc.exists()) {
        const schemaData = schemaDoc.data().schemas || {};
        setSchemas({
          stepSchema: schemaData.stepSchema || null,
          sectionSchema: schemaData.sectionSchema || null,
        });
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to load steps and sections");
    }
    setIsLoading(false);
  };

  const updateFormData = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
    setHasChanges(true);
  };

  const handleSave = async ({ allowZeroPrerequisites = false } = {}) => {
    // Validation
    if (!formData.name.trim()) {
      setError("Name is required");
      return;
    }
    
    // For new documents, generate ID from name
    const docId = isNew ? toCamelCase(formData.name) : formData.id.trim();
    
    if (!docId) {
      setError("ID is required (enter a valid name)");
      return;
    }

    if (hasZeroPrerequisiteSummary(formData) && !allowZeroPrerequisites) {
      setZeroPrerequisiteDialogOpen(true);
      return;
    }

    setZeroPrerequisiteDialogOpen(false);

    setIsSaving(true);
    setError("");

    try {
      // Check uniqueness for new documents
      if (isNew) {
        const existingDoc = await getDoc(doc(db, "documentTypes", docId));
        if (existingDoc.exists()) {
          setError(`A document type with ID "${docId}" already exists. Please use a different name.`);
          setIsSaving(false);
          return;
        }
      }
      
      // Version handling
      const newVersion = isNew ? 1 : currentVersion + 1;
      
      // Create version snapshot
      const versionSnapshot = {
        version: newVersion,
        name: formData.name.trim(),
        savedAt: new Date().toISOString(),
        savedBy: currentUser?.email || "unknown",
        stepWorkflow: formData.stepWorkflow.map((step, index) => ({
          stepRef: step.stepRef || step.id,
          stepId: step.stepId || step.name || step.id,
          order: index + 1,
        })),
        sectionLayout: formData.sectionLayout.map((section, index) => ({
          sectionRef: section.sectionRef || section.id,
          sectionId: section.sectionId || section.name || section.id,
          order: index + 1,
        })),
      };
      
      const docData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        format: formData.format,
        category: CATEGORY_FROM_SUBCATEGORY[formData.subcategory] || formData.category,
        subcategory: formData.subcategory,
        eta: Number(formData.eta) || 120,
        status: formData.status,
        exports: formData.exports,
        menu: formData.menu,
        stepWorkflow: formData.stepWorkflow.map((step, index) => ({
          stepRef: step.stepRef || step.id,
          stepId: step.stepId || step.name || step.id,
          order: index + 1,
        })),
        sectionLayout: formData.sectionLayout.map((section, index) => ({
          sectionRef: section.sectionRef || section.id,
          sectionId: section.sectionId || section.name || section.id,
          order: index + 1,
        })),
        notes: formData.notes || "",
        prerequisites: formData.prerequisites || [],
        version: newVersion,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.email || "unknown",
      };

      if (isNew) {
        docData.createdAt = serverTimestamp();
        docData.createdBy = currentUser?.email || "unknown";
        docData.versions = [versionSnapshot];
        docData.publishedVersion = null;
        docData.isLocked = false;
        docData.active = true;
      } else {
        // Append to existing versions
        const existingVersions = savedDocumentType.current?.versions || [];
        docData.versions = [...existingVersions, versionSnapshot];
        // Preserve publishedVersion and isLocked
        docData.publishedVersion = documentType?.publishedVersion || null;
        docData.isLocked = documentType?.isLocked || false;
      }

      // Clean the data before saving to remove any undefined values
      const cleanedDocData = cleanForFirestore(docData);
      
      if (isNew) {
        await saveConfigurationRevision(db, doc(db, "documentTypes", docId), null, cleanedDocData, {
          create: true,
          duplicateMessage: `A document type with ID "${docId}" already exists. Please use a different name.`,
        });
      } else {
        await saveConfigurationRevision(db, doc(db, "documentTypes", docId), savedDocumentType.current, cleanedDocData);
      }
      // A clone's source can contain fields omitted from the new record.
      // Only existing-record updates retain those unedited stored fields.
      savedDocumentType.current = { ...(isNew ? {} : savedDocumentType.current), ...cleanedDocData, id: docId };
      setFormData(prev => ({ ...prev, id: docId }));
      setSavedVersion(newVersion); // Track the saved version for publish
      setHasChanges(false);
      onSave?.(savedDocumentType.current);
    } catch (err) {
      console.error("Error saving document type:", err);
      setError("Failed to save: " + err.message);
    }
    setIsSaving(false);
  };
  
  // Handle publishing the current version
  const handlePublish = async () => {
    if (isNew || !documentType) return;
    if (hasChanges) {
      setError('Save your changes before publishing.');
      return;
    }
    
    setIsPublishing(true);
    setError("");
    try {
      await saveConfigurationRevision(db, doc(db, "documentTypes", formData.id.trim()), savedDocumentType.current, {
        publishedVersion: currentVersion,
        status: "published",
        isLocked: true,
        updatedAt: serverTimestamp()
      });
      onSave?.();
      onBack();
    } catch (err) {
      console.error("Error publishing document type:", err);
      setError("Failed to publish: " + err.message);
    }
    setIsPublishing(false);
  };
  
  // Handle unlock confirmation
  const handleUnlockConfirm = () => {
    setSessionUnlocked(true);
    setUnlockDialogOpen(false);
  };

  // Drag-and-drop handlers for step workflow
  const moveStep = useCallback((dragIndex, hoverIndex) => {
    setFormData(prev => {
      const newWorkflow = [...prev.stepWorkflow];
      const [removed] = newWorkflow.splice(dragIndex, 1);
      newWorkflow.splice(hoverIndex, 0, removed);
      return { ...prev, stepWorkflow: newWorkflow };
    });
    setHasChanges(true);
  }, []);

  const removeStep = useCallback((index) => {
    setFormData(prev => ({
      ...prev,
      stepWorkflow: prev.stepWorkflow.filter((_, i) => i !== index),
    }));
    setHasChanges(true);
  }, []);

  const addStep = useCallback((step, atIndex = null) => {
    setFormData(prev => {
      // Use stepRef for new auto-ID, keep stepId for backward compatibility
      const newStep = { stepRef: step.id, stepId: step.name || step.id, ...step };
      if (atIndex !== null) {
        const newWorkflow = [...prev.stepWorkflow];
        newWorkflow.splice(atIndex, 0, newStep);
        return { ...prev, stepWorkflow: newWorkflow };
      }
      return { ...prev, stepWorkflow: [...prev.stepWorkflow, newStep] };
    });
    setHasChanges(true);
  }, []);

  // Drag-and-drop handlers for section layout
  const moveSection = useCallback((dragIndex, hoverIndex) => {
    setFormData(prev => {
      const newLayout = [...prev.sectionLayout];
      const [removed] = newLayout.splice(dragIndex, 1);
      newLayout.splice(hoverIndex, 0, removed);
      return { ...prev, sectionLayout: newLayout };
    });
    setHasChanges(true);
  }, []);

  const removeSection = useCallback((index) => {
    setFormData(prev => ({
      ...prev,
      sectionLayout: prev.sectionLayout.filter((_, i) => i !== index),
    }));
    setHasChanges(true);
  }, []);

  const addSection = useCallback((section, atIndex = null) => {
    setFormData(prev => {
      // Use sectionRef for new auto-ID, keep sectionId for backward compatibility
      const newSection = { sectionRef: section.id, sectionId: section.name || section.id, ...section };
      if (atIndex !== null) {
        const newLayout = [...prev.sectionLayout];
        newLayout.splice(atIndex, 0, newSection);
        return { ...prev, sectionLayout: newLayout };
      }
      return { ...prev, sectionLayout: [...prev.sectionLayout, newSection] };
    });
    setHasChanges(true);
  }, []);

  // Get enriched workflow/layout items with display names and full data for previews
  const getEnrichedWorkflow = () => {
    return formData.stepWorkflow.map(item => {
      // Use stepRef (new auto-ID) first, fallback to stepId matched by name
      const stepRef = item.stepRef;
      const stepId = item.stepId || item.id;
      const step = stepRef 
        ? steps.find(s => s.id === stepRef)
        : steps.find(s => s.id === stepId || s.name === stepId);
      
      // Look up the prompt by promptName (which is the Firestore document ID)
      const promptName = step?.promptName;
      const promptData = promptName ? prompts.find(p => p.id === promptName) : null;
      
      // Debug logging
      if (step && !promptData && promptName) {
        console.log(`Step "${stepId}" has promptName "${promptName}" but no matching prompt found. Available prompt IDs:`, prompts.map(p => p.id).slice(0, 5));
      }
      
      // Spread step first, then item, then override with explicit values
      return {
        ...step,
        ...item,
        id: step?.id || stepRef || stepId,  // Use actual step ID (auto-generated)
        stepRef: stepRef || step?.id,        // Preserve the ref for editing
        // Use step's displayName or name, fallback to ID only if both are missing
        displayName: step?.displayName || step?.name || stepId,
        type: step?.type || "",
        output: step?.output || "",
        isLocked: step?.isLocked || false,
        // Get prompt content from the prompts collection
        promptName: promptName || "",
        prompt: promptData?.prompt || "",
        prePrompt: promptData?.prePrompt || "",
        postPrompt: promptData?.postPrompt || "",
        hasPrompt: !!promptData?.prompt,
      };
    });
  };

  const getEnrichedLayout = () => {
    return formData.sectionLayout.map(item => {
      // Use sectionRef (new auto-ID) first, fallback to sectionId matched by name
      const sectionRef = item.sectionRef;
      const sectionId = item.sectionId || item.id;
      const section = sectionRef
        ? sections.find(s => s.id === sectionRef)
        : sections.find(s => s.id === sectionId || s.name === sectionId);
      return {
        ...section,
        ...item,
        id: section?.id || sectionRef || sectionId,  // Use actual section ID (auto-generated)
        sectionRef: sectionRef || section?.id,        // Preserve the ref for editing
        // Use section's title for display
        displayName: section?.title || section?.displayName || section?.name || sectionId,
        type: section?.type || "",
        dataField: section?.dataField || "",
        isLocked: section?.isLocked || false,
      };
    });
  };

  // Resolve the binding before opening, including reference-only legacy items.
  const openEditItem = async (item, type) => {
    try {
      const snapshot = await getDocs(collection(db, 'documentDataFields'));
      const fields = snapshot.docs.map(field => ({ ...field.data(), id: field.id }));
      setEditingItem(item);
      setEditingItemType(type);
      setEditFormData(initializeFieldBinding(
        applySchemaDefaults(type === 'step' ? schemas.stepSchema : schemas.sectionSchema, item),
        fields, type === 'step'));
      setError('');
      setEditSessionUnlocked(false);
      setEditModalOpen(true);
    } catch (err) {
      console.error('Error loading field binding:', err);
      setError('Failed to load steps and sections');
    }
  };

  // Edit handlers
  const handleEditStep = (item) => {
    // Use stepRef (new auto-ID) or match by name as fallback
    const step = steps.find(s => s.id === item.stepRef || s.id === item.id || s.name === item.id);
    if (step) {
      openEditItem(step, 'step');
    }
  };

  const handleEditSection = (item) => {
    // Use sectionRef (new auto-ID) or match by name as fallback
    const section = sections.find(s => s.id === item.sectionRef || s.id === item.id || s.name === item.id);
    if (section) {
      openEditItem(section, 'section');
    }
  };

  const handleEditSave = async () => {
    setIsEditSaving(true);
    try {
      const collectionName = editingItemType === "step" ? "documentSteps" : "documentSections";
      const currentVersion = editingItem.version || 1;
      const newVersion = currentVersion + 1;
      
      // Remove system fields that shouldn't be spread from editFormData
      const { 
        versions: _versions, 
        createdAt: _createdAt, 
        updatedAt: _updatedAt,
        version: _version,
        publishedVersion: _publishedVersion,
        isLocked: _isLocked,
        ...formFieldsOnly 
      } = editFormData;
      
      // Deep clean form fields for Firestore (removes undefined at all levels)
      const cleanedData = cleanForFirestore(formFieldsOnly);
      
      const versionSnapshot = configurationSnapshot(cleanedData, {
        version: newVersion,
        savedAt: new Date().toISOString(),
        savedBy: currentUser?.email || "unknown",
      });
      const existingVersions = (editingItem.versions || []).map(cleanConfigurationSnapshot);
      
      const dataToSave = {
        ...cleanedData,
        version: newVersion,
        versions: [...existingVersions, versionSnapshot],
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.email || "unknown",
      };
      
      console.log("Saving step/section data:", JSON.stringify(dataToSave, null, 2));
      
      await saveConfigurationRevision(db, doc(db, collectionName, editingItem.id), editingItem, dataToSave, {
        dataField: {
          fieldName: editingItemType === 'step' ? cleanedData.output : cleanedData.dataField,
          fieldKey: editingItemType === 'step' ? 'output' : 'dataField',
          referenceId: editingItemType === 'step' ? cleanedData.outputFieldRef : cleanedData.dataFieldRef,
          displayName: cleanedData.displayName || cleanedData.title || cleanedData.name || editingItem.id,
        },
      });
      
      // Refresh data
      await fetchData();
      setEditModalOpen(false);
      setEditingItem(null);
    } catch (err) {
      console.error("Error saving:", err);
      setError("Failed to save: " + err.message);
    }
    setIsEditSaving(false);
  };

  // Publish handler for edit modal items
  const handleEditPublish = async () => {
    if (!editingItem) return;
    
    setIsEditPublishing(true);
    try {
      const collectionName = editingItemType === "step" ? "documentSteps" : "documentSections";
      const currentVersion = editingItem.version || 1;
      
      // Remove system fields that shouldn't be spread from editFormData
      const { 
        versions: _versions, 
        createdAt: _createdAt, 
        updatedAt: _updatedAt,
        version: _version,
        publishedVersion: _publishedVersion,
        isLocked: _isLocked,
        ...formFieldsOnly 
      } = editFormData;
      
      // Deep clean form fields for Firestore (removes undefined at all levels)
      const cleanedData = cleanForFirestore(formFieldsOnly);
      
      const versionSnapshot = configurationSnapshot(cleanedData, {
        version: currentVersion,
        savedAt: new Date().toISOString(),
        savedBy: currentUser?.email || "unknown",
      });
      const existingVersions = (editingItem.versions || []).map(cleanConfigurationSnapshot);
      
      // Save form data AND publish in one operation
      await saveConfigurationRevision(db, doc(db, collectionName, editingItem.id), editingItem, {
        ...cleanedData,
        version: currentVersion,
        publishedVersion: currentVersion,
        isLocked: true,
        versions: [...existingVersions, versionSnapshot],
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.email || "unknown",
      }, {
        dataField: {
          fieldName: editingItemType === 'step' ? cleanedData.output : cleanedData.dataField,
          fieldKey: editingItemType === 'step' ? 'output' : 'dataField',
          referenceId: editingItemType === 'step' ? cleanedData.outputFieldRef : cleanedData.dataFieldRef,
          displayName: cleanedData.displayName || cleanedData.title || cleanedData.name || editingItem.id,
        },
      });
      
      await fetchData();
      setEditModalOpen(false);
      setEditingItem(null);
    } catch (err) {
      console.error("Error publishing:", err);
      setError("Failed to publish: " + err.message);
    }
    setIsEditPublishing(false);
  };

  // Unlock handler for edit modal
  const handleEditUnlockConfirm = () => {
    setEditSessionUnlocked(true);
    setEditUnlockDialogOpen(false);
  };

  // Create new step/section handlers
  const handleCreateNew = (type) => {
    setCreateItemType(type);
    setCreateModalOpen(true);
  };

  // Prompt view handler
  const handleViewPrompt = (item) => {
    setViewingPromptItem(item);
    setPromptViewOpen(true);
  };

  if (isLoading) {
    return <LinearProgress />;
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <Box sx={{ 
        position: "relative",
        height: "calc(100vh - 200px)", 
        overflow: "auto",
        mx: -2.5, // Compensate for parent padding
        px: 2.5,
      }}>
        {/* Header with Back button, Tabs, and Save */}
        <Box sx={{ 
          display: "flex", 
          alignItems: "center", 
          gap: 2, 
          mb: 2, 
          borderBottom: 1, 
          borderColor: "divider",
          position: "sticky",
          top: 0,
          bgcolor: "white",
          zIndex: 10,
          py: 1
        }}>
          <IconButton onClick={handleBack} sx={{ mr: 1 }} aria-label="Back to document types">
            <ArrowLeft size={20} />
          </IconButton>
          
          {/* Version and Lock indicators */}
          {!isNew && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Chip 
                label={`v${currentVersion}`} 
                size="small" 
                variant="outlined"
                sx={{ fontSize: 'var(--text-xs)' }}
              />
              {isLocked && (
                <Tooltip title="Locked - click to unlock for this session" arrow>
                  <IconButton size="small" onClick={() => setUnlockDialogOpen(true)} sx={{ p: 0.5 }}>
                    <Lock size={16} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
          
          <Tabs 
            value={activeTab} 
            onChange={(e, v) => setActiveTab(v)}
            sx={{ flex: 1 }}
          >
            <Tab icon={<GitBranch size={16} />} iconPosition="start" label={`AI Steps (${getEnrichedWorkflow().length})`} />
            <Tab icon={<Layout size={16} />} iconPosition="start" label={`Document Sections (${getEnrichedLayout().length})`} />
            <Tab icon={<Settings size={16} />} iconPosition="start" label="Settings" />
            <Tab icon={<StickyNote size={16} />} iconPosition="start" label="Notes" />
          </Tabs>

          {!isNew && hasUnpublishedChanges && (
            <Button
              variant="outlined"
              color="success"
              startIcon={<Send size={16} />}
              onClick={handlePublish}
              disabled={isPublishing || isLocked || hasChanges}
            >
              {isPublishing ? "Publishing..." : isLocked ? "Unlock to Publish" : "Publish"}
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={isLocked ? <Lock size={16} /> : <Save size={16} />}
            onClick={isLocked ? () => setUnlockDialogOpen(true) : () => handleSave()}
            disabled={isSaving || (!hasChanges && !isLocked)}
            sx={{ ml: 1 }}
          >
            {isSaving ? "Saving..." : isLocked ? "Unlock to Edit" : "Save"}
          </Button>
        </Box>
        
        {/* Unlock Confirmation Dialog */}
        <Dialog open={unlockDialogOpen} onClose={() => setUnlockDialogOpen(false)} sx={{ zIndex: 10002 }}>
          <DialogTitle>Unlock for Editing?</DialogTitle>
          <DialogContent>
            <Typography>
              This document type is locked because it has been published. 
              Unlocking will allow you to make changes for this session only.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUnlockDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUnlockConfirm} variant="contained" startIcon={<Unlock size={16} />}>
              Unlock
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={zeroPrerequisiteDialogOpen}
          onClose={() => setZeroPrerequisiteDialogOpen(false)}
          maxWidth="xs"
          fullWidth
          sx={{ zIndex: 10002 }}
        >
          <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, color: "warning.dark" }}>
            <AlertTriangle size={20} />
            Save summary without prerequisites?
          </DialogTitle>
          <DialogContent>
            <Typography>
              This summary type has no prerequisites and will not be able to generate. Save anyway?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setZeroPrerequisiteDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={() => handleSave({ allowZeroPrerequisites: true })}
              variant="contained"
              color="warning"
              disabled={isSaving}
            >
              Save Anyway
            </Button>
          </DialogActions>
        </Dialog>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {hasChanges && (
          <Alert severity="info" sx={{ mb: 2 }}>
            You have unsaved changes
          </Alert>
        )}

        {/* Settings Tab */}
        {activeTab === 2 && (
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2 }}>
              <TextField
                label="Name"
                value={formData.name}
                onChange={(e) => updateFormData({ name: e.target.value })}
                required
                size="small"
                sx={{ gridColumn: "span 2" }}
              />
              {isNew && (
                <TextField
                  label="ID (auto-generated)"
                  value={toCamelCase(formData.name)}
                  size="small"
                  slotProps={{ input: { readOnly: true } }}
                  helperText="Generated from name"
                  sx={{ 
                    gridColumn: "span 2",
                    "& .MuiInputBase-input": { 
                      bgcolor: "var(--color-light-background)",
                      fontFamily: "monospace"
                    }
                  }}
                />
              )}
              <FormControl size="small">
                <InputLabel>Format</InputLabel>
                <Select
                  value={formData.format}
                  label="Format"
                  onChange={(e) => updateFormData({ format: e.target.value })}
                >
                  {sortByDisplayLabel(FORMATS, format => format.label).map(f => (
                    <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.subcategory}
                  label="Category"
                  onChange={(e) => updateFormData({ 
                    subcategory: e.target.value,
                    category: CATEGORY_FROM_SUBCATEGORY[e.target.value] || 1
                  })}
                >
                  {sortByDisplayLabel(SUBCATEGORIES, category => category.label).map(c => (
                    <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => updateFormData({ description: e.target.value })}
                multiline
                rows={2}
                size="small"
                sx={{ gridColumn: "span 2" }}
              />

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Exports</Typography>
                {EXPORT_OPTIONS.map(opt => (
                  <FormControlLabel
                    key={opt.value}
                    control={
                      <Checkbox
                        checked={formData.exports.includes(opt.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            updateFormData({ exports: [...formData.exports, opt.value] });
                          } else {
                            updateFormData({ exports: formData.exports.filter(v => v !== opt.value) });
                          }
                        }}
                        size="small"
                      />
                    }
                    label={opt.label}
                  />
                ))}
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Menu Visibility</Typography>
                {MENU_OPTIONS.map(opt => (
                  <FormControlLabel
                    key={opt.value}
                    control={
                      <Checkbox
                        checked={formData.menu.includes(opt.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            updateFormData({ menu: [...formData.menu, opt.value] });
                          } else {
                            updateFormData({ menu: formData.menu.filter(v => v !== opt.value) });
                          }
                        }}
                        size="small"
                      />
                    }
                    label={opt.label}
                  />
                ))}
              </Box>

              {/* Prerequisites Section */}
              <PrerequisitesEditor
                prerequisites={formData.prerequisites}
                currentSubcategory={formData.subcategory}
                onChange={(prerequisites) => updateFormData({ prerequisites })}
                warnWhenEmpty={formData.format === "summary"}
              />
            </Box>
          </Paper>
        )}

        {/* Notes Tab */}
        {activeTab === 3 && (
          <Paper sx={{ p: 3, height: "calc(100vh - 220px)", display: "flex", flexDirection: "column" }}>
            <AdminNoteEditor
              value={formData.notes}
              onChange={(notes) => updateFormData({ notes })}
              placeholder="Add notes about this document type..."
              minHeight="100%"
            />
          </Paper>
        )}

        {/* Steps Tab - Workflow Layout */}
        {activeTab === 0 && (
          <Box sx={{ display: "grid", gridTemplateColumns: "280px 1fr", gap: 2, height: "calc(100vh - 200px)" }}>
            <Paper sx={{ p: 1.5, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <AvailablePalette
                items={steps}
                selectedIds={formData.stepWorkflow.map(s => s.stepRef || s.stepId || s.id)}
                onAdd={addStep}
                label="AI Steps"
                isStep={true}
                type={ItemTypes.STEP}
                onCreateNew={() => handleCreateNew("step")}
              />
            </Paper>
            <Paper sx={{ p: 2, display: "flex", flexDirection: "column", overflow: "hidden", alignItems: "center" }}>
              {(() => {
                const workflow = getEnrichedWorkflow();
                const totalTime = workflow.reduce((sum, step) => {
                  const promptData = step.promptName ? prompts.find(p => p.id === step.promptName) : null;
                  const time = promptData?.lastTestStats?.responseTime;
                  return sum + (time ? parseFloat(time) : 0);
                }, 0);
                return (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                      {formData.name || formData.id || "Workflow"} ({workflow.length} steps)
                    </Typography>
                    {totalTime > 0 && (
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 600,
                          color: totalTime < 60 
                            ? 'success.main' 
                            : totalTime < 180 
                              ? 'warning.main' 
                              : 'error.main'
                        }}
                      >
                        Total: {totalTime.toFixed(1)}s
                      </Typography>
                    )}
                  </Box>
                );
              })()}
              <Box sx={{ flex: 1, overflowY: "auto", width: "100%" }}>
                <WorkflowFlow
                  items={getEnrichedWorkflow()}
                  moveItem={moveStep}
                  removeItem={removeStep}
                  onEditItem={handleEditStep}
                  onViewPrompt={handleViewPrompt}
                  type={ItemTypes.STEP}
                  isStep={true}
                  onInsertAt={addStep}
                  prompts={prompts}
                />
              </Box>
            </Paper>
          </Box>
        )}

        {/* Sections Tab - Workflow Layout */}
        {activeTab === 1 && (
          <Box sx={{ display: "grid", gridTemplateColumns: "280px 1fr", gap: 2, height: "calc(100vh - 200px)" }}>
            <Paper sx={{ p: 1.5, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <AvailablePalette
                items={sections}
                selectedIds={formData.sectionLayout.map(s => s.sectionRef || s.sectionId || s.id)}
                onAdd={addSection}
                label="Document Sections"
                isStep={false}
                type={ItemTypes.SECTION}
                onCreateNew={() => handleCreateNew("section")}
              />
            </Paper>
            <Paper sx={{ p: 2, display: "flex", flexDirection: "column", overflow: "hidden", alignItems: "center" }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500, alignSelf: "flex-start" }}>
                Layout ({getEnrichedLayout().length} sections)
              </Typography>
              <Box sx={{ flex: 1, overflowY: "auto", width: "100%" }}>
                <WorkflowFlow
                  items={getEnrichedLayout()}
                  moveItem={moveSection}
                  removeItem={removeSection}
                  onEditItem={handleEditSection}
                  onViewPrompt={() => {}}
                  type={ItemTypes.SECTION}
                  isStep={false}
                  onInsertAt={addSection}
                />
              </Box>
            </Paper>
          </Box>
        )}

        {/* Edit Modal */}
        <ModalPortal isOpen={editModalOpen}>
          <Dialog 
            open={editModalOpen} 
            onClose={() => setEditModalOpen(false)}
            maxWidth="md"
            fullWidth
            sx={{ zIndex: 'var(--z-modal-backdrop)' }}
          >
          {(() => {
            const isEditItemLocked = editingItem?.isLocked && !editSessionUnlocked;
            const editItemVersion = editingItem?.version || 1;
            const editItemPublishedVersion = editingItem?.publishedVersion || null;
            const hasEditUnpublishedChanges = !editItemPublishedVersion || editItemVersion !== editItemPublishedVersion;
            
            return (
              <>
                <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Edit2 size={18} />
                    Edit {editingItemType === "step" ? "Step" : "Section"}: {editingItem?.displayName || editingItem?.name || editingItem?.id}
                    <Chip 
                      label={`v${editItemVersion}`} 
                      size="small" 
                      variant="outlined"
                      sx={{ fontSize: 'var(--text-xs)' }}
                    />
                    {isEditItemLocked && (
                      <Tooltip title="Locked - click to unlock for this session" arrow>
                        <IconButton size="small" onClick={() => setEditUnlockDialogOpen(true)} sx={{ p: 0.5 }}>
                          <Lock size={16} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </DialogTitle>
                <DialogContent>
                  <Alert severity="warning" sx={{ mb: 2, mt: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <AlertTriangle size={16} />
                      Changes will affect all document types using this {editingItemType}.
                    </Box>
                  </Alert>
                  
                  {editingItemType === "step" && schemas.stepSchema && (
                    <SchemaFormRenderer
                      schema={schemas.stepSchema}
                      data={editFormData}
                      onChange={handleEditFormChange}
                      errors={{}}
                      excludeFields={schemas.stepSchema?.fields?.filter(f => f.hidden).map(f => f.key) || []}
                      disabled={isEditItemLocked}
                    />
                  )}

                  {editingItemType === "section" && schemas.sectionSchema && (
                    <SchemaFormRenderer
                      schema={schemas.sectionSchema}
                      data={editFormData}
                      onChange={handleEditFormChange}
                      errors={{}}
                      excludeFields={schemas.sectionSchema?.fields?.filter(f => f.hidden).map(f => f.key) || []}
                      disabled={isEditItemLocked}
                    />
                  )}

                  {((editingItemType === "step" && !schemas.stepSchema) || 
                    (editingItemType === "section" && !schemas.sectionSchema)) && (
                    <Alert severity="info">
                      Editor unavailable.
                    </Alert>
                  )}

                  {error && (
                    <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError("")}>
                      {error}
                    </Alert>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setEditModalOpen(false)}>Cancel</Button>
                  {hasEditUnpublishedChanges && (
                    <Button 
                      onClick={handleEditPublish} 
                      variant="outlined" 
                      color="success"
                      disabled={isEditPublishing}
                      startIcon={<Send size={16} />}
                    >
                      {isEditPublishing ? "Publishing..." : "Publish"}
                    </Button>
                  )}
                  <Button 
                    variant="contained" 
                    onClick={isEditItemLocked ? () => setEditUnlockDialogOpen(true) : handleEditSave}
                    disabled={isEditSaving}
                    startIcon={isEditItemLocked ? <Lock size={16} /> : null}
                  >
                    {isEditSaving ? "Saving..." : isEditItemLocked ? "Unlock" : "Save Changes"}
                  </Button>
                </DialogActions>
              </>
            );
          })()}
          </Dialog>
        </ModalPortal>

        {/* Unlock Dialog for Edit Modal - separate from main dialog to avoid nesting issues */}
        <Dialog open={editUnlockDialogOpen} onClose={() => setEditUnlockDialogOpen(false)} sx={{ zIndex: 10002 }}>
          <DialogTitle>Unlock for Editing?</DialogTitle>
          <DialogContent>
            <Typography>
              This {editingItemType} is locked because it has been published. 
              Unlocking will allow you to make changes for this session only.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditUnlockDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleEditUnlockConfirm} variant="contained" startIcon={<Unlock size={16} />}>
              Unlock
            </Button>
          </DialogActions>
        </Dialog>

        {/* Create New Step/Section Modal - uses shared ItemFormDialog */}
        {createModalOpen && (
          <ItemFormDialog
            open={createModalOpen}
            onClose={() => setCreateModalOpen(false)}
            currentUser={currentUser}
            config={createItemType === "step" 
              ? { collectionName: "documentSteps", schemaKey: "stepSchema", entityName: "Step" }
              : { collectionName: "documentSections", schemaKey: "sectionSchema", entityName: "Section" }
            }
            schema={createItemType === "step" ? schemas.stepSchema : schemas.sectionSchema}
            onSave={() => {
              setCreateModalOpen(false);
              fetchData();
            }}
          />
        )}

        {/* Prompt View Modal (Read-only) */}
        <ModalPortal isOpen={promptViewOpen}>
          <Dialog 
            open={promptViewOpen} 
            onClose={() => setPromptViewOpen(false)}
            maxWidth="md"
            fullWidth
            sx={{ zIndex: 'var(--z-modal-backdrop)' }}
          >
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FileText size={18} />
              Prompt: {viewingPromptItem?.displayName || viewingPromptItem?.name || viewingPromptItem?.id}
            </DialogTitle>
            <DialogContent>
              {viewingPromptItem?.prePrompt && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Pre-Prompt
                  </Typography>
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 2, 
                      bgcolor: "var(--color-prompt-pre-bg)", 
                      fontFamily: "monospace", 
                      fontSize: "var(--text-sm)",
                      whiteSpace: "pre-wrap",
                      maxHeight: 150,
                      overflowY: "auto",
                    }}
                  >
                    {viewingPromptItem.prePrompt}
                  </Paper>
                </Box>
              )}
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Main Prompt
                </Typography>
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                    bgcolor: "var(--color-prompt-bg)", 
                    fontFamily: "monospace", 
                    fontSize: "var(--text-sm)",
                    whiteSpace: "pre-wrap",
                    maxHeight: 400,
                    overflowY: "auto",
                  }}
                >
                  {viewingPromptItem?.prompt || "(No prompt defined)"}
                </Paper>
              </Box>

              {viewingPromptItem?.postPrompt && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Post-Prompt
                  </Typography>
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 2, 
                      bgcolor: "var(--color-prompt-pre-bg)", 
                      fontFamily: "monospace", 
                      fontSize: "var(--text-sm)",
                      whiteSpace: "pre-wrap",
                      maxHeight: 150,
                      overflowY: "auto",
                    }}
                  >
                    {viewingPromptItem.postPrompt}
                  </Paper>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPromptViewOpen(false)}>Close</Button>
            </DialogActions>
          </Dialog>
        </ModalPortal>
      </Box>
    </DndProvider>
  );
};

export default AdminDocumentTypeEditor;
